import { Archive, CircleCheck, CircleDot, History, Package, RefreshCw, Save, Trash2 } from 'lucide-react'

import { QueryState } from '@krp/sale-ui'
import { Button, SelectField, TextField } from '@krp/ui-kit'

import type { PriceHistoryRow } from '../model/prices'
import type { OwnProductPricesActions, OwnProductPricesVm } from '../model/types'
import { CommandError, DateField, Problem } from './parts'
import './own-nomenclature.css'
import { ICON_SIZE, ICON_SIZE_SMALL } from '../../../ownIcons'

function PriceTable({
  caption,
  rows,
  vm,
  actions,
  withDelete,
}: {
  caption: string
  rows: readonly PriceHistoryRow[]
  vm: OwnProductPricesVm
  actions: OwnProductPricesActions
  withDelete: boolean
}) {
  const write = vm.writeDisabledReason === null
  return (
    <div className="own-scroll">
      <table className="own-table">
        <caption className="own-muted">{caption}</caption>
        <thead>
          <tr>
            <th>Вид цены</th>
            <th data-numeric="true">Цена</th>
            <th>Действует с</th>
            {withDelete && <th>На {vm.onText}</th>}
            <th>Кто и когда записал</th>
            {withDelete && <th>Действия</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.priceTypeName}</td>
              <td data-numeric="true">{row.priceText}</td>
              <td>{row.validFromText}</td>
              {withDelete && (
                <td>
                  {row.current ? (
                    <span className="own-badge" data-kind="current">
                      <CircleDot size={ICON_SIZE_SMALL} aria-hidden="true" />
                      действует
                    </span>
                  ) : (
                    <span className="own-muted">—</span>
                  )}
                </td>
              )}
              <td>
                {row.setBy}, {row.setAtText}
              </td>
              {withDelete && (
                <td>
                  {vm.confirmDeleteKey === row.key ? (
                    <span className="own-row">
                      <span>Удалить запись цены с {row.validFromText}?</span>
                      <Button variant="danger" busy={vm.deleting} onClick={actions.confirmDelete}>
                        Удалить
                      </Button>
                      <Button variant="ghost" disabled={vm.deleting} onClick={actions.cancelDelete}>
                        Отмена
                      </Button>
                    </span>
                  ) : (
                    <Button variant="ghost" icon={<Trash2 size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={() => actions.askDelete(row.key)}>
                      Удалить запись
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Цены одного товара своей базы: действующие на выбранную дату по каждому виду, вся история записей и новая цена с даты.
 * Цена действует с начала суток магазина и до следующей записи того же вида; запись на ту же дату заменяет прежнюю.
 */
export function OwnProductPricesScreen({ vm, actions }: { vm: OwnProductPricesVm; actions: OwnProductPricesActions }) {
  const write = vm.writeDisabledReason === null
  const form = vm.form
  return (
    <section className="own-screen" aria-labelledby="own-product-prices-title">
      <header className="own-toolbar">
        <h2 id="own-product-prices-title">Цены: {vm.title}</h2>
        <Button variant="ghost" icon={<Package size={ICON_SIZE} aria-hidden="true" />} onClick={actions.openProduct}>
          Карточка товара
        </Button>
        <Button variant="ghost" icon={<RefreshCw size={ICON_SIZE} aria-hidden="true" />} onClick={actions.reload}>
          Обновить
        </Button>
      </header>
      <QueryState query={vm.productQuery} emptyText="Товара нет" onRetry={actions.reload} what="Товар" />
      {vm.skuText !== '' && <p className="own-muted">{vm.skuText}</p>}
      {vm.archived && (
        <p className="own-problem" role="note">
          <Archive size={ICON_SIZE_SMALL} aria-hidden="true" />
          Товар в архиве: новые цены ему не ставятся.
        </p>
      )}
      {vm.done !== null && (
        <p className="own-problem" role="status">
          <CircleCheck size={ICON_SIZE_SMALL} aria-hidden="true" />
          {vm.done}
        </p>
      )}

      <section className="own-section" aria-labelledby="own-price-set-title">
        <h3 id="own-price-set-title">Новая цена</h3>
        {vm.writeDisabledReason !== null && (
          <p className="own-muted" role="note">
            Запись выключена. {vm.writeDisabledReason}
          </p>
        )}
        <div className="own-row">
          <SelectField
            label="Вид цены"
            value={form.draft.priceTypeId}
            options={vm.kindOptions}
            onChange={(priceTypeId) => actions.draftChanged({ ...form.draft, priceTypeId })}
          />
          <DateField label="Действует с" value={form.draft.validFrom} onChange={(validFrom) => actions.draftChanged({ ...form.draft, validFrom })} />
          <TextField label="Цена, ₽" value={form.draft.price} placeholder="0,00" onChange={(price) => actions.draftChanged({ ...form.draft, price })} />
        </div>
        <Problem text={form.problem} />
        {form.error !== null && <CommandError title="Цена не записана" error={form.error} />}
        <div className="own-row">
          <Button
            variant="primary"
            icon={<Save size={ICON_SIZE} aria-hidden="true" />}
            busy={form.saving}
            disabled={!write || form.problem !== null}
            onClick={actions.save}
          >
            Записать цену
          </Button>
        </div>
      </section>

      <section className="own-section" aria-labelledby="own-price-on-title">
        <div className="own-section__head">
          <h3 id="own-price-on-title">Действуют на {vm.onText}</h3>
          <DateField label="Дата" value={vm.on} onChange={actions.onChanged} />
        </div>
        <QueryState query={vm.current.query} emptyText="На эту дату цен нет ни по одному виду" onRetry={actions.reload} what="Цены на дату" />
        {vm.current.rows.length > 0 && <PriceTable caption={`Видов с ценой: ${vm.current.rows.length}`} rows={vm.current.rows} vm={vm} actions={actions} withDelete={false} />}
      </section>

      <section className="own-section" aria-labelledby="own-price-history-title">
        <h3 id="own-price-history-title">
          <History size={ICON_SIZE} aria-hidden="true" /> История цен
        </h3>
        {vm.deleteError !== null && <CommandError title="Запись не удалена" error={vm.deleteError} />}
        <QueryState query={vm.history.query} emptyText="Цен у товара ещё не было" onRetry={actions.reload} what="История" />
        {vm.history.rows.length > 0 && <PriceTable caption={`Записей: ${vm.history.rows.length}`} rows={vm.history.rows} vm={vm} actions={actions} withDelete />}
      </section>
    </section>
  )
}
